import React from "react";
import MaterialTable from "material-table";
import {Column} from "material-table";
import {ThemeProvider, createTheme} from "@mui/material/styles";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import tableIcons from "../Displays/MaterialTableIcons";
import {
	useGetGlobalInventoryReport
} from "../../queries/GlobalInventoryReport";
import { useGetOperationalTeams }  from '../../queries/AttorneyInfo';
import PageLoader from '../../component/Displays/Loading';
import {
	TokenManagerInterface,
	AccessToken
} from "@okta/okta-auth-js";
import {useOktaReducer} from "../../store/okta";
import './style.scss';

const defaultMaterialTheme = createTheme();
const formatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
});

function formatDateTimeString(dateString: string, includeTime = false) {
	// 1. Parse the input string into a Date object. The format provided is standard
	// and recognized by the Date constructor.
	const date = new Date(dateString);

	// 2. Validate the date object. getTime() returns NaN for invalid dates.
	if (isNaN(date.getTime())) {
		return "Invalid Date Input: Please ensure the format is correct.";
	}

	// 3. Extract Date components using UTC methods to ensure the output matches
	// the explicit GMT time in the input, regardless of the user's local timezone.

	// Get UTC Year
	const year = date.getUTCFullYear();

	// Get UTC Month (0-indexed, so add 1) and pad with a leading zero if needed
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');

	// Get UTC Day and pad
	const day = String(date.getUTCDate()).padStart(2, '0');

	// Start building the output string
	let output = `${year}-${month}-${day}`;

	// 4. Optionally include the time components
	if (includeTime) {
		// Get UTC Hours, Minutes, and Seconds and pad them
		const hours = String(date.getUTCHours()).padStart(2, '0');
		const minutes = String(date.getUTCMinutes()).padStart(2, '0');
		const seconds = String(date.getUTCSeconds()).padStart(2, '0');

		// Append time to the date
		output += ` ${hours}:${minutes}:${seconds}`;
	}

	return output;
}

const GlobalInventoryReportTable = () => {

  const authClient: any = useOktaReducer();
	const [userToken, setUserToken] = React.useState<any>("")

  const getInfo = async () => {
	const tokenManager: TokenManagerInterface = authClient.tokenManager;
	const accessToken: AccessToken = (await tokenManager.get(
	  "accessToken"
	)) as AccessToken;

	setUserToken(accessToken.accessToken);
  };

  React.useEffect(() => {
	getInfo();
  }, [userToken]);

	const {data, isFetching, isError} = useGetGlobalInventoryReport(userToken);
	const {data: teamData, isFetching: teamFetching, isError: teamError } = useGetOperationalTeams();
	const [selectedTeam, setSelectedValue] = React.useState('All');

	if (isError || !data || !teamData) {
		return <div>Error loading members.</div>;
	}

	const columns: Column<any>[] = [
		{
			title: "Case", 
			field: "case", 
			width: "5%",
			render: (rowData: any) => 
				<div className="tooltip">{rowData["case"]}
					<span className="tooltiptext">{rowData["liability_description"]}</span>
				</div>
		},
		{
			title: "Data Missing", 
			field: "data_missing", 
			width: "5%",
		},
		{title: "Matter", width: "5%", field: "matter"},
		{title: "Case Type", width: "5%", field: "case_type"},
		{title: "Attorney Team", width: "5%", field: "attorney_team"},
		{title: "Liability Description", width: "5%", field: "liability_description", hidden: true},
		{
			title: "Date Case Retained", 
			width: "5%", 
			field: "date_case_retained",
		},
		{title: "Days in Inventory", width: "5%", field: "days_in_inventory"},
		{title: "Current Step", width: "5%", field: "current_step"},
		{title: "Referral Fee", width: "5%", field: "referall_fee"},
		{
			title: "Anticipated Attorney Fee", 
			field: "anticipated_attorney_fee",
			width: "5%",
			render: (rowData: any) => formatter.format(Number(rowData["anticipated_attorney_fee"])),
			cellStyle: { padding: "5px" }
		},
		{title: "Case Class", field: "case_class"},
		{
			title: "Projected Date of Anticipated Attorney Fee", 
			field: "projected_date_of_anticipated_attorney_fee",
			width: "200px"
		},
		{
			title: "Date of Loss", 
			field: "date_of_loss",
			width: "5%",
		},
		{title: "PFS Served", width: "5%", field: "pfs_served"},
		{title: "Mediation Scheduled", width: "5%", field: "mediation_scheduled"},
		{title: "Trial Order", width: "5%", field: "trial_order"},
		{title: "Conference Requested", width: "5%", field: "conference_requested"},
		{title: "Open / Closed", width: "5%", field: "open_closed", defaultSort: 'desc', hidden: true},
	];

	if (isFetching || teamFetching) 
		return <PageLoader />;

	const totalsRow = {
		"anticipated_attorney_fee": 0,
		"attorney_team": "",
		"data_missing": "",
		"case": "Total",
		"case_class": "",
		"case_type": "",
		"conference_requested": "",
		"current_step": "",
		"date_case_retained": "",
		"date_of_loss": "1900-01-01",
		"days_in_inventory": "",
		"liability_description": "",
		"litigation_date": "",
		"matter": "",
		"mediation_scheduled": "",
		"open_closed": true,
		"pfs_served": "",
		"projected_date_of_anticipated_attorney_fee": "1900-01-01",
		"referall_fee": "",
		"trial_order": ""
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedValue(event.target.value);
	};

	const formattedData = data.map( (col: any) => {
		const t = JSON.parse(JSON.stringify(col));
		let pastDate = new Date(t["date_case_retained"]);

		// Create a Date object for the current day.
		let today = new Date();

		// Set the time for both dates to midnight UTC to get a clean day count.
		// This prevents discrepancies from time zone offsets.
		let pastDateUTC = Date.UTC(pastDate.getFullYear(), pastDate.getMonth(), pastDate.getDate());
		let todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

		// Calculate the difference in milliseconds.
		let msDifference = todayUTC - pastDateUTC;

		// Convert the difference from milliseconds to days.
		let oneDayInMs = 1000 * 60 * 60 * 24;
		let daysDifference = Math.round(msDifference / oneDayInMs);

		if (pastDate.getFullYear() < 2000) {
			daysDifference = 0;
		}

		t["days_in_inventory"] = daysDifference || 0;
		t["data_missing"] = t["date_case_retained"] === "None" || formatDateTimeString(t["projected_date_of_anticipated_attorney_fee"]) === "1900-01-01" ? "Y" : "N";
		t["date_case_retained"] = t["date_case_retained"] === "None" ? "" : t["date_case_retained"];
		t["projected_date_of_anticipated_attorney_fee"] = formatDateTimeString(t["projected_date_of_anticipated_attorney_fee"], false) === "1900-01-01" ? "" : formatDateTimeString(t["projected_date_of_anticipated_attorney_fee"], false);
		t["date_of_loss"] = formatDateTimeString(t["date_of_loss"], false) === "1900-01-01" ? "" : formatDateTimeString(t["date_of_loss"], false);
	
		return t;
	});

	[...formattedData]
		.filter( (col: any) => selectedTeam === 'All' ? true : (selectedTeam === col["attorney_team"]))
		.forEach( (value: any) => {
		totalsRow["anticipated_attorney_fee"] += value["anticipated_attorney_fee"]
	})

	return (
		<ThemeProvider theme={defaultMaterialTheme}>
			<FormControl>
				<FormLabel id="demo-row-radio-buttons-group-label">Attorney Team</FormLabel>
				<RadioGroup
					row
					aria-labelledby="demo-row-radio-buttons-group-label"
					name="row-radio-buttons-group"
				>
					<FormControlLabel value={"All"} control={<Radio checked={selectedTeam === 'All'} onChange={handleChange} />} label={"All"} />
					{
						teamData.map( (team: any) => <FormControlLabel value={team.team_name} control={<Radio checked={selectedTeam === team.team_name} onChange={handleChange} />} label={team.team_name} />	)
					}
				</RadioGroup>
			</FormControl>
			<MaterialTable
				title={`Global Inventory Report`}
				columns={columns}
				data={[...formattedData.filter( (col: any) => selectedTeam === 'All' ? true : (selectedTeam === col["attorney_team"])), totalsRow]}
				isLoading={isFetching}
				icons={tableIcons}
				options={{
					pageSize: formattedData?.length || 0,
					pageSizeOptions: [],
					paging: false,					
					search: true,
					exportButton: true,
					tableLayout: "fixed",
					maxBodyHeight: '650px',
					headerStyle: {
						backgroundColor: "#3f51b5",
						color: "#FFF",
						fontWeight: "bold",
						padding: "6px",
						position: 'sticky', 
						top: 0,
					},
					rowStyle: (rowData) => ({
						padding: "6px",
						backgroundColor:
							rowData.status !== "subscribed" ? "white" : "inherit",
					}),
					
				}}
			/>
		</ThemeProvider>
	);
};

export default GlobalInventoryReportTable;
